/**
 * React Query hooks for invoice operations
 */

import { useQueries, useQuery } from "@tanstack/react-query";
import { useInvoicesClient } from "./useInvoicesClient";

interface UseInvoicesOptions {
  limit?: number;
  offset?: number;
  registryId?: string | null;
  /** Filter by invoice issue type (PROFORMA or VAT) */
  issueType?: "PROFORMA" | "VAT";
  /** Filter for correction invoices only */
  isCorrection?: boolean;
}

export function useInvoices(options: UseInvoicesOptions = {}) {
  const client = useInvoicesClient();
  const { limit = 50, offset = 0, registryId, issueType, isCorrection } = options;

  // Build filter object only with defined values
  const filter: {
    registryId?: string;
    issueType?: "PROFORMA" | "VAT";
    isCorrection?: boolean;
  } = {};

  if (registryId) {
    filter.registryId = registryId;
  }
  if (issueType) {
    filter.issueType = issueType;
  }
  if (isCorrection !== undefined) {
    filter.isCorrection = isCorrection;
  }

  return useQuery({
    queryKey: ["invoices", { limit, offset, registryId, issueType, isCorrection }],
    queryFn: async () => {
      console.log("[useInvoices] Fetching invoices...", { limit, offset, registryId, issueType, isCorrection });
      const result = await client.listInvoices({
        filter: Object.keys(filter).length > 0 ? filter : undefined,
        pagination: {
          limit,
          offset,
        },
      });
      console.log("[useInvoices] Fetched", result.total, "invoices");
      return result;
    },
  });
}

/**
 * Hook to fetch invoice counts for multiple registries in parallel
 */
export function useInvoiceCountsByRegistry(registryIds: string[]) {
  const client = useInvoicesClient();

  const queries = useQueries({
    queries: registryIds.map((registryId) => ({
      queryKey: ["invoiceCount", registryId],
      queryFn: async () => {
        const result = await client.listInvoices({
          filter: { registryId },
          pagination: { limit: 1, offset: 0 },
        });
        return { registryId, count: result.total };
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const counts = new Map(
    queries
      .filter((q): q is typeof q & { data: { registryId: string; count: number } } => q.data != null)
      .map((q) => [q.data.registryId, q.data.count] as const),
  );

  return {
    counts,
    isLoading: queries.some((q) => q.isLoading),
  };
}

/**
 * Hook to fetch total invoice count (all registries)
 */
export function useTotalInvoiceCount() {
  const client = useInvoicesClient();

  return useQuery({
    queryKey: ["invoiceCount", "all"],
    queryFn: async () => {
      const result = await client.listInvoices({
        pagination: { limit: 1, offset: 0 },
      });
      return result.total;
    },
    staleTime: 5 * 60 * 1000,
  });
}
